import React, { useState } from 'react';
import { Modal, Alert, Form, Flex, Input, Button, Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import { DownOutlined, ArrowRightOutlined } from '@ant-design/icons';

import DeviceTypeRadioSelector from '~components/DeviceTypeRadioSelector';
import DropdownWithUpsell from '~components/DropdownWithUpsell';
import { GscSitemapImportModal, useGetGscIntegrationsStatusQuery } from '~features/gsc-integrations';
import { useUpgradePlan } from '~features/UpgradePlan';
import useRevisitIntervalOptions from '../useRevisitIntervalOptions';
import RevisitIntervalFooter from './RevisitIntervalFooter';

type ImportSitemapModalProps = {
  open?: boolean;
  onImport: (data: { sitemapUrl: string; adaptiveType: 'desktop' | 'mobile' | 'all'; revisitInterval: number }) => void;
  onClose: () => void;
  errorMsg?: string;
  confirmLoading?: boolean;
  mobileOptimizedEnabled?: boolean;
};

function ImportSitemapModal({
  open,
  onImport,
  onClose,
  errorMsg,
  confirmLoading,
  mobileOptimizedEnabled,
}: ImportSitemapModalProps) {
  const { token } = theme.useToken();
  const { openUpgradePlan } = useUpgradePlan();
  const options = useRevisitIntervalOptions();
  const defaultOption = options[options.length - 1];
  const [revisitInterval, setRevisitInterval] = useState(defaultOption);
  const [isGscImportModalOpen, setIsGscImportModalOpen] = useState(false);

  const { data: gscIntegrationStatus } = useGetGscIntegrationsStatusQuery();
  const isGscConnected = gscIntegrationStatus?.data?.isConnected ?? false;

  const openGscImportModal = () => {
    onClose();
    setIsGscImportModalOpen(true);
  };

  const gscImportLabel = (
    <>
      Or import from Google Search Console <ArrowRightOutlined />
    </>
  );

  const onSubmit = (values: {
    sitemapUrl: string;
    adaptiveType: 'desktop' | 'mobile' | 'all';
    revisitInterval: number;
  }) => {
    onImport({ ...values, revisitInterval: revisitInterval.value });
  };

  return (
    <>
      <Modal
        closable
        title="Import sitemap"
        open={open}
        okText="Import"
        okButtonProps={{ htmlType: 'submit', form: 'importSitemap' }}
        onCancel={onClose}
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        destroyOnHidden
      >
        <Form name="importSitemap" layout="vertical" onFinish={onSubmit} initialValues={{ adaptiveType: 'desktop' }}>
          <Form.Item
            name="sitemapUrl"
            label="Sitemap URL"
            tooltip="Please provide a valid url to a sitemap.xml file."
            rules={[
              {
                required: true,
                message: 'Please provide a valid url',
              },
            ]}
            extra={
              <div style={{ marginTop: token.marginXS }}>
                {isGscConnected ? (
                  <Typography.Link style={{ color: token.colorPrimary }} onClick={openGscImportModal}>
                    {gscImportLabel}
                  </Typography.Link>
                ) : (
                  <Link to="/integrations" style={{ color: token.colorPrimary }}>
                    {gscImportLabel}
                  </Link>
                )}
              </div>
            }
          >
            <Input placeholder="https://example.com/sitemap.xml" type="url"></Input>
          </Form.Item>

          <Form.Item
            name="adaptiveType"
            label="Device type"
            htmlFor="adaptiveType"
            tooltip="Choose whether to cache pages for desktop, mobile, or both. Optimizing for both improves SEO visibility, but it will also double your rendering volume."
          >
            <DeviceTypeRadioSelector mobileOptimizedEnabled={!!mobileOptimizedEnabled} />
          </Form.Item>

          <Form.Item name="revisitInterval" label="Revisit interval">
            <DropdownWithUpsell
              footerElement={<RevisitIntervalFooter />}
              selectedKey={revisitInterval.key}
              options={options}
              onOptionSelected={(option) => setRevisitInterval(option)}
              onUpsellClicked={(option) =>
                openUpgradePlan('sitemaps', option.upsellPlanId, {
                  ctaLocation: 'Import Sitemap Modal - Revisit Interval Dropdown',
                  ctaName: 'Upgrade',
                })
              }
            >
              <Button loading={false} block>
                <Flex justify="space-between" flex={1}>
                  <Typography.Text>{revisitInterval.label}</Typography.Text>
                  <DownOutlined />
                </Flex>
              </Button>
            </DropdownWithUpsell>
          </Form.Item>

          {errorMsg && <Alert showIcon type="error" message={errorMsg} />}
        </Form>
      </Modal>

      <GscSitemapImportModal open={isGscImportModalOpen} onClose={() => setIsGscImportModalOpen(false)} />
    </>
  );
}

export default ImportSitemapModal;
